import { API_BASE, ApiError, apiFetch, isDeployApiConfigured, tokenStorage } from './api';
import type {
  Certificate, CertificatePayload, CertificateUpdate, CloudflareAccount, CloudflareZone, DeploymentStep,
  ExternalTarget, ImportReport, NetworkJob, NetworkSettings, NetworkStatus, ProjectDomains, ProxyNode,
  SshAuthType, SshTestResult, TargetSummary, Vhost, VhostPayload,
} from '@/deploy/types/network';

/** Réponse texte (conf nginx) : apiFetch ne lit que le JSON. */
async function fetchText(path: string, init: RequestInit = {}): Promise<string> {
  if (!isDeployApiConfigured()) throw new ApiError(0, "URL de l'API recube-cicd non configurée.");
  const token = tokenStorage.get();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Accept: 'text/plain',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text || `HTTP ${res.status}`;
    try { msg = (JSON.parse(text) as { message?: string }).message ?? msg; } catch { /* texte brut */ }
    throw new ApiError(res.status, msg);
  }
  return text;
}

const p = (projectId: string) => `/api/projects/${projectId}/network`;

/** Réseau du projet actif (vhosts, certificats), filtré côté CICD par domaines principaux. */
export const networkService = {
  domains: (projectId: string) => apiFetch<ProjectDomains>(`${p(projectId)}/domains`),

  listVhosts: (projectId: string) => apiFetch<Vhost[]>(`${p(projectId)}/vhosts`),
  vhostConfig: (projectId: string, domain: string) => fetchText(`${p(projectId)}/vhosts/${domain}/config`),
  previewVhost: (projectId: string, payload: VhostPayload) =>
    fetchText(`${p(projectId)}/vhosts/preview`, { method: 'POST', body: JSON.stringify(payload) }),
  createVhost: (projectId: string, payload: VhostPayload) =>
    apiFetch<NetworkJob>(`${p(projectId)}/vhosts`, { method: 'POST', json: payload }),
  updateVhost: (projectId: string, domain: string, payload: VhostPayload) =>
    apiFetch<NetworkJob>(`${p(projectId)}/vhosts/${domain}`, { method: 'PUT', json: payload }),
  adoptVhost: (projectId: string, domain: string, payload: VhostPayload) =>
    apiFetch<NetworkJob>(`${p(projectId)}/vhosts/${domain}/adopt`, { method: 'POST', json: payload }),
  deleteVhost: (projectId: string, domain: string, deleteDns: boolean, deleteCertificate: boolean) =>
    apiFetch<NetworkJob>(`${p(projectId)}/vhosts/${domain}?deleteDns=${deleteDns}&deleteCertificate=${deleteCertificate}`,
      { method: 'DELETE' }),

  listCertificates: (projectId: string, kind?: 'INTERNAL' | 'EXTERNAL') =>
    apiFetch<Certificate[]>(`${p(projectId)}/certificates${kind ? `?kind=${kind}` : ''}`),
  getCertificate: (projectId: string, id: number) => apiFetch<Certificate>(`${p(projectId)}/certificates/${id}`),
  createCertificate: (projectId: string, payload: CertificatePayload) =>
    apiFetch<NetworkJob>(`${p(projectId)}/certificates`, { method: 'POST', json: payload }),
  updateCertificate: (projectId: string, id: number, patch: CertificateUpdate) =>
    apiFetch<Certificate>(`${p(projectId)}/certificates/${id}`, { method: 'PUT', json: patch }),
  deleteCertificate: (projectId: string, id: number) =>
    apiFetch<void>(`${p(projectId)}/certificates/${id}`, { method: 'DELETE' }),
  renewCertificate: (projectId: string, id: number) =>
    apiFetch<NetworkJob>(`${p(projectId)}/certificates/${id}/renew`, { method: 'POST' }),
  deployCertificate: (projectId: string, id: number) =>
    apiFetch<NetworkJob>(`${p(projectId)}/certificates/${id}/deploy`, { method: 'POST' }),
  getSteps: (projectId: string, id: number) => apiFetch<DeploymentStep[]>(`${p(projectId)}/certificates/${id}/steps`),
  setSteps: (projectId: string, id: number, steps: DeploymentStep[]) =>
    apiFetch<DeploymentStep[]>(`${p(projectId)}/certificates/${id}/steps`, { method: 'PUT', json: steps }),

  targets: (projectId: string) => apiFetch<TargetSummary[]>(`${p(projectId)}/targets`),
  job: (projectId: string, jobId: number) => apiFetch<NetworkJob>(`${p(projectId)}/jobs/${jobId}`),
};

export interface NodePayload {
  name?: string;
  host?: string;
  port?: number;
  username?: string;
  enabled?: boolean;
  canary?: boolean;
}

export interface TargetPayload {
  name?: string;
  host?: string;
  port?: number;
  username?: string;
  authType?: SshAuthType;
  /** Mot de passe ou clé privée. Vide à l'édition : inchangé. */
  secret?: string;
}

/** Réglages réseau communs à tous les projets (admin). */
export const networkAdminService = {
  status: () => apiFetch<NetworkStatus>('/api/network/status'),
  getSettings: () => apiFetch<NetworkSettings>('/api/network/settings'),
  saveSettings: (s: NetworkSettings) => apiFetch<NetworkSettings>('/api/network/settings', { method: 'PUT', json: s }),
  zones: () => apiFetch<CloudflareZone[]>('/api/network/zones'),

  accounts: () => apiFetch<CloudflareAccount[]>('/api/network/cloudflare/accounts'),
  createAccount: (name: string, apiToken: string) =>
    apiFetch<CloudflareAccount | { account: CloudflareAccount; warning: string }>('/api/network/cloudflare/accounts',
      { method: 'POST', json: { name, apiToken } }),
  updateAccount: (id: number, patch: { name?: string; apiToken?: string }) =>
    apiFetch<CloudflareAccount>(`/api/network/cloudflare/accounts/${id}`, { method: 'PUT', json: patch }),
  deleteAccount: (id: number) => apiFetch<void>(`/api/network/cloudflare/accounts/${id}`, { method: 'DELETE' }),
  syncAccount: (id: number) => apiFetch<CloudflareAccount>(`/api/network/cloudflare/accounts/${id}/sync`, { method: 'POST' }),

  nodes: () => apiFetch<ProxyNode[]>('/api/network/nodes'),
  nodesPublicKey: () => apiFetch<{ publicKey: string }>('/api/network/nodes/ssh-public-key'),
  createNode: (n: NodePayload) => apiFetch<ProxyNode>('/api/network/nodes', { method: 'POST', json: n }),
  updateNode: (id: number, n: NodePayload) => apiFetch<ProxyNode>(`/api/network/nodes/${id}`, { method: 'PUT', json: n }),
  deleteNode: (id: number) => apiFetch<void>(`/api/network/nodes/${id}`, { method: 'DELETE' }),
  testNode: (id: number) => apiFetch<SshTestResult>(`/api/network/nodes/${id}/test`, { method: 'POST' }),
  resetNodeHostKey: (id: number) => apiFetch<ProxyNode>(`/api/network/nodes/${id}/reset-host-key`, { method: 'POST' }),

  targets: () => apiFetch<ExternalTarget[]>('/api/network/targets'),
  createTarget: (t: TargetPayload) => apiFetch<ExternalTarget>('/api/network/targets', { method: 'POST', json: t }),
  updateTarget: (id: number, t: TargetPayload) => apiFetch<ExternalTarget>(`/api/network/targets/${id}`, { method: 'PUT', json: t }),
  deleteTarget: (id: number) => apiFetch<void>(`/api/network/targets/${id}`, { method: 'DELETE' }),
  testTarget: (id: number) => apiFetch<SshTestResult>(`/api/network/targets/${id}/test`, { method: 'POST' }),
  resetTargetHostKey: (id: number) => apiFetch<ExternalTarget>(`/api/network/targets/${id}/reset-host-key`, { method: 'POST' }),

  reloadNginx: () => apiFetch<NetworkJob>('/api/network/nginx/reload', { method: 'POST' }),
  renewalCheck: () => apiFetch<NetworkJob>('/api/network/renewal/check', { method: 'POST' }),
  testNotification: () => apiFetch<{ sent: boolean }>('/api/network/notifications/test', { method: 'POST' }),
  importNginx: (dryRun: boolean) => apiFetch<ImportReport>(`/api/network/import?dryRun=${dryRun}`, { method: 'POST' }),
  job: (id: number) => apiFetch<NetworkJob>(`/api/network/jobs/${id}`),
};
